/**
 * IonAccessBanner — global, top-of-page banner shown when Ion is
 * unavailable for a non-trivial reason (kill-switch flipped, account
 * disabled by an admin, network failure). Mounted once in AppShell.
 *
 * The banner is intentionally narrow in scope: FREE-tier and no-subscription
 * users simply don't see the surface (handled by isIonVisible gating in
 * IonSurface) — there's no value in nagging them site-wide. Anything where
 * the user *had* access and now doesn't is loud enough to deserve a banner.
 */

import { chatAccess } from '../../state/chatStore.js'
import { t } from '../../i18n/index.js'
import { bootstrapChatAccess } from '../../services/ionAccessService.js'

const BANNER_REASONS = new Set(['kill_switch', 'disabled_by_admin', 'network'])

export function IonAccessBanner() {
  const access = chatAccess.value
  if (access.loading) return null
  if (!access.reason) return null
  if (!BANNER_REASONS.has(access.reason)) return null

  const title = t(`ion.access.${access.reason}.title`)
  const body = t(`ion.access.${access.reason}.body`)

  function handleRetry() {
    bootstrapChatAccess().catch(() => {
      /* error state already reflected in chatAccess */
    })
  }

  return (
    <div class="ion-access-banner" role="alert" aria-live="polite">
      <div class="ion-access-banner__content">
        <strong>{title}</strong>
        <span>{body}</span>
      </div>
      {access.reason === 'network' && (
        <button
          type="button"
          class="ion-access-banner__retry"
          onClick={handleRetry}
        >
          Retry
        </button>
      )}
    </div>
  )
}
